import type { ClientMessage } from './protocol';

/** The workspace and tab in front on this device, and the server that owns it. */
export type Foreground = { serverId: string; workspace: string; tab?: string | null };

/**
 * Tells each server which workspace is in front, so a push is not sent for what someone is looking at and the meter
 * can credit a typed answer to this device. It skips a send when nothing changed, so it must be told when a
 * connection was replaced: the server keeps the foreground per connection and a new one starts with none.
 */
export function foregroundReporter(send: (serverId: string, msg: ClientMessage) => void) {
  let reported: Foreground | null = null;
  const same = (a: Foreground | null, b: Foreground | null) =>
    a?.serverId === b?.serverId && a?.workspace === b?.workspace && a?.tab === b?.tab;
  const tell = (f: Foreground) => send(f.serverId, { type: 'foreground', workspace: f.workspace, tab: f.tab });
  return {
    report(next: Foreground | null) {
      if (same(next, reported)) return;
      if (reported && reported.serverId !== next?.serverId) send(reported.serverId, { type: 'foreground', workspace: null });
      if (next) tell(next);
      reported = next;
    },
    /** `serverId` has a new connection (a restart, a dropped link): it lost what it was told, so tell it again. */
    reconnected(serverId: string) {
      if (reported?.serverId === serverId) tell(reported);
    },
  };
}
